import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
    LayoutDashboard,
    Users,
    Gamepad2,
    Image,
    ShoppingBag,
    Calendar,
    CreditCard,
    Lightbulb,
    CheckCircle,
    RefreshCw,
    Loader2,
    LogOut,
    ChevronLeft,
    Shield,
    Zap,
    Trash2
} from "lucide-react";
import {
    Sidebar,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarGroupLabel,
    SidebarHeader,
    SidebarMenu,
    SidebarMenuButton,
    SidebarMenuItem,
    SidebarProvider,
    SidebarInset,
    SidebarTrigger
} from "@/components/ui/sidebar";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/useAuth";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface AdminLayoutProps {
    children: React.ReactNode;
}

export const AdminLayout = ({ children }: AdminLayoutProps) => {
    const location = useLocation();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [refreshingStats, setRefreshingStats] = useState(false);
    const [resettingXp, setResettingXp] = useState(false);
    const [showResetConfirm, setShowResetConfirm] = useState(false);
    const [clearingMissions, setClearingMissions] = useState(false);
    const [showClearMissionsConfirm, setShowClearMissionsConfirm] = useState(false);

    const menuItems = [
        { title: "Dashboard", icon: LayoutDashboard, path: "/admin" },
        { title: "Members", icon: Users, path: "/admin/members" },
        { title: "Games", icon: Gamepad2, path: "/admin/games" },
        { title: "Banners", icon: Image, path: "/admin/banners" },
        { title: "Products", icon: ShoppingBag, path: "/admin/products" },
        { title: "Events", icon: Calendar, path: "/admin/events" },
        { title: "Subscriptions", icon: CreditCard, path: "/admin/subscriptions" },
        { title: "Beta Ideas", icon: Lightbulb, path: "/admin/beta-ideas" },
    ];

    const handleRefreshStats = async () => {
        try {
            setRefreshingStats(true);
            const { error } = await supabase.functions.invoke('refresh-platform-stats');
            if (error) throw error;
            toast.success("Platform statistics refreshed successfully");
        } catch (error: any) {
            console.error("Error refreshing stats:", error);
            toast.error("Failed to refresh statistics: " + error.message);
        } finally {
            setRefreshingStats(false);
        }
    };

    const handleResetXp = async () => {
        try {
            setResettingXp(true);
            const { error } = await supabase
                .from('profiles')
                .update({ xp: 0, player_level: 1 })
                .eq('user_type', 'player');

            if (error) throw error;
            toast.success("All players XP and Level have been reset");
            setShowResetConfirm(false);
        } catch (error: any) {
            console.error("Error resetting XP:", error);
            toast.error("Failed to reset XP: " + error.message);
        } finally {
            setResettingXp(false);
        }
    };

    const handleDeleteMissions = async () => {
        try {
            setClearingMissions(true);
            const { error } = await supabase
                .from('player_missions')
                .delete()
                .neq('id', '00000000-0000-0000-0000-000000000000'); // Delete all workaround

            if (error) throw error;
            toast.success("All player missions have been cleared");
            setShowClearMissionsConfirm(false);
        } catch (error: any) {
            console.error("Error clearing missions:", error);
            toast.error("Failed to clear missions: " + error.message);
        } finally {
            setClearingMissions(false);
        }
    };

    return (
        <SidebarProvider>
            <div className="flex min-h-screen w-full bg-background/95">
                <Sidebar
                    className="border-r border-white/5 bg-black md:bg-black/40 backdrop-blur-xl transition-all duration-300 group-data-[side=left]:border-r"
                >
                    <SidebarHeader className="h-20 flex !px-6 !pb-0 justify-center">
                        <Link to="/admin" className="flex items-center gap-3 font-bold text-xl tracking-tight">
                            Admin Panel
                        </Link>
                    </SidebarHeader>

                    <SidebarContent className="px-3 custom-scrollbar">
                        <SidebarGroup>
                            <SidebarGroupLabel className="px-3 mb-2 text-[10px] uppercase font-bold tracking-widest text-muted-foreground/50">
                                Management
                            </SidebarGroupLabel>
                            <SidebarMenu className="gap-1">
                                {menuItems.map((item) => (
                                    <SidebarMenuItem key={item.path}>
                                        <SidebarMenuButton
                                            asChild
                                            isActive={location.pathname === item.path || (item.path !== "/admin" && location.pathname.startsWith(item.path))}
                                            tooltip={item.title}
                                            className={`
                                                relative h-11 px-3 rounded-xl transition-all duration-200 group
                                                data-[active=true]:bg-primary/10 data-[active=true]:text-primary
                                                hover:bg-white/5 hover:text-white
                                            `}
                                        >
                                            <Link to={item.path} className="flex items-center gap-3">
                                                <item.icon className={`
                                                    w-5 h-5 transition-transform duration-200 
                                                    group-active:scale-95 group-hover:scale-110
                                                    ${location.pathname === item.path ? 'text-primary' : 'text-muted-foreground group-hover:text-white'}
                                                `} />
                                                <span className={`
                                                    font-medium
                                                    ${location.pathname === item.path ? 'text-primary' : 'text-muted-foreground/80 group-hover:text-white'}
                                                `}>
                                                    {item.title}
                                                </span>
                                                {location.pathname === item.path && (
                                                    <div className="absolute left-0 w-1 h-5 bg-primary rounded-full" />
                                                )}
                                            </Link>
                                        </SidebarMenuButton>
                                    </SidebarMenuItem>
                                ))}
                            </SidebarMenu>
                        </SidebarGroup>

                        <SidebarGroup className="mt-4">
                            <SidebarGroupLabel className="px-3 mb-2 text-[10px] uppercase font-bold tracking-widest text-muted-foreground/50">
                                Actions
                            </SidebarGroupLabel>
                            <SidebarMenu>
                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        onClick={handleRefreshStats}
                                        disabled={refreshingStats}
                                        tooltip="Refresh Stats"
                                        className="h-11 px-3 rounded-xl hover:bg-white/5 transition-all duration-200 group items-center"
                                    >
                                        <div className={`
                                            p-1.5 rounded-lg transition-colors
                                            ${refreshingStats ? 'bg-primary/20' : 'bg-white/5 group-hover:bg-primary/20'}
                                        `}>
                                            {refreshingStats ? (
                                                <Loader2 className="w-4 h-4 animate-spin text-primary" />
                                            ) : (
                                                <RefreshCw className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                                            )}
                                        </div>
                                        <span className="font-medium text-muted-foreground/80 group-hover:text-white ml-3">
                                            Refresh Stats
                                        </span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        onClick={() => setShowResetConfirm(true)}
                                        disabled={resettingXp}
                                        tooltip="Reset All Players XP"
                                        className="h-11 px-3 rounded-xl hover:bg-white/5 transition-all duration-200 group items-center"
                                    >
                                        <div className={`
                                            p-1.5 rounded-lg transition-colors
                                            ${resettingXp ? 'bg-amber-500/20' : 'bg-white/5 group-hover:bg-amber-500/20'}
                                        `}>
                                            {resettingXp ? (
                                                <Loader2 className="w-4 h-4 animate-spin text-amber-500" />
                                            ) : (
                                                <Zap className="w-4 h-4 text-muted-foreground group-hover:text-amber-500 transition-colors" />
                                            )}
                                        </div>
                                        <span className="font-medium text-muted-foreground/80 group-hover:text-white ml-3">
                                            Reset Players XP
                                        </span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>

                                <SidebarMenuItem>
                                    <SidebarMenuButton
                                        onClick={() => setShowClearMissionsConfirm(true)}
                                        disabled={clearingMissions}
                                        tooltip="Clear All Player Missions"
                                        className="h-11 px-3 rounded-xl hover:bg-white/5 transition-all duration-200 group items-center"
                                    >
                                        <div className={`
                                            p-1.5 rounded-lg transition-colors
                                            ${clearingMissions ? 'bg-red-500/20' : 'bg-white/5 group-hover:bg-red-500/20'}
                                        `}>
                                            {clearingMissions ? (
                                                <Loader2 className="w-4 h-4 animate-spin text-red-500" />
                                            ) : (
                                                <Trash2 className="w-4 h-4 text-muted-foreground group-hover:text-red-500 transition-colors" />
                                            )}
                                        </div>
                                        <span className="font-medium text-muted-foreground/80 group-hover:text-white ml-3">
                                            Clear Player Missions
                                        </span>
                                    </SidebarMenuButton>
                                </SidebarMenuItem>
                            </SidebarMenu>
                        </SidebarGroup>
                    </SidebarContent>
                </Sidebar>

                <SidebarInset className="flex flex-col bg-transparent">
                    <Header />
                    <main className="flex-1 p-4 md:p-10 lg:p-12 max-w-[1600px] mx-auto w-full">
                        <div className="flex items-center gap-4 mb-2">
                            <SidebarTrigger className="md:hidden hover:bg-white/5 transition-colors" />
                            {location.pathname !== "/admin" && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => navigate(-1)}
                                    className="gap-2 text-muted-foreground hover:text-white transition-colors group"
                                >
                                    <ChevronLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
                                    <span className="font-medium">Retour</span>
                                </Button>
                            )}
                        </div>
                        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 fill-mode-both">
                            {children}
                        </div>
                    </main>
                    <Footer />
                </SidebarInset>
            </div>

            <AlertDialog open={showResetConfirm} onOpenChange={setShowResetConfirm}>
                <AlertDialogContent className="bg-slate-900 border-white/10">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold flex items-center gap-2">
                            <Zap className="w-5 h-5 text-amber-500" />
                            Confirm XP Reset
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            This is a <span className="text-red-500 font-bold uppercase">dangerous action</span>.
                            It will permanently reset the Experience (XP) and Level of <span className="text-white font-semibold">ALL players</span> back to 0 and Level 1.
                            This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel
                            disabled={resettingXp}
                            className="bg-white/5 border-white/10 hover:bg-white/10 hover:text-white transition-colors"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleResetXp();
                            }}
                            disabled={resettingXp}
                            className="bg-red-600 hover:bg-red-700 text-white border-none transition-colors px-6"
                        >
                            {resettingXp ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Resetting...
                                </>
                            ) : (
                                "Yes, Reset All XP"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog open={showClearMissionsConfirm} onOpenChange={setShowClearMissionsConfirm}>
                <AlertDialogContent className="bg-slate-900 border-white/10">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="text-xl font-bold flex items-center gap-2">
                            <Trash2 className="w-5 h-5 text-red-500" />
                            Confirm Missions Clear
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-slate-400">
                            This is a <span className="text-red-500 font-bold uppercase">dangerous action</span>.
                            It will permanently delete <span className="text-white font-semibold">ALL recorded player missions</span> (completed and pending) for every user.
                            This cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel
                            disabled={clearingMissions}
                            className="bg-white/5 border-white/10 hover:bg-white/10 hover:text-white transition-colors"
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                handleDeleteMissions();
                            }}
                            disabled={clearingMissions}
                            className="bg-red-600 hover:bg-red-700 text-white border-none transition-colors px-6"
                        >
                            {clearingMissions ? (
                                <>
                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                    Clearing...
                                </>
                            ) : (
                                "Yes, Clear All Missions"
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </SidebarProvider >
    );
};
